import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getUpload, getUploadStatus } from '../../services/api'
import { Upload, Layer } from '../../types'
import { ArrowLeft, Clock, CheckCircle, XCircle, Loader, Map } from 'lucide-react'

export default function UploadView() {
  const { uploadId } = useParams()
  const navigate = useNavigate()
  const [upload, setUpload] = useState<Upload | null>(null)
  const [layers, setLayers] = useState<Layer[]>([])
  const [status, setStatus] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!uploadId) return
    loadUpload()
  }, [uploadId])

  // Poll status if processing
  useEffect(() => {
    if (!uploadId || status === 'completed' || status === 'failed') return
    const interval = setInterval(() => {
      getUploadStatus(parseInt(uploadId))
        .then(res => {
          setStatus(res.data.status.status)
          if (res.data.status.status === 'completed') {
            loadUpload()
          }
        })
        .catch(console.error)
    }, 5000)
    return () => clearInterval(interval)
  }, [uploadId, status])

  const loadUpload = async () => {
    try {
      const res = await getUpload(parseInt(uploadId!))
      setUpload(res.data.upload)
      setLayers(res.data.layers)
      setStatus(res.data.upload.status)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  if (!upload) {
    return <div className="text-center py-16 text-gray-400">Upload not found</div>
  }

  const statusConfig = {
    pending: { icon: <Clock size={20} />, color: 'text-yellow-500', bg: 'bg-yellow-50', label: 'Pending' },
    processing: { icon: <Loader size={20} className="animate-spin" />, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Processing' },
    completed: { icon: <CheckCircle size={20} />, color: 'text-green-500', bg: 'bg-green-50', label: 'Completed' },
    failed: { icon: <XCircle size={20} />, color: 'text-red-500', bg: 'bg-red-50', label: 'Failed' },
  }

  const currentStatus = statusConfig[upload.status as keyof typeof statusConfig]

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-50 rounded-lg text-navy-600">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2 className="text-xl font-bold text-navy-800">Upload #{upload.id}</h2>
          <p className="text-sm text-gray-500">{upload.original_filename}</p>
        </div>
      </div>

      {/* Status Card */}
      <div className={`card ${currentStatus.bg}`}>
        <div className="flex items-center gap-3">
          <span className={currentStatus.color}>{currentStatus.icon}</span>
          <div>
            <p className={`font-semibold ${currentStatus.color}`}>{currentStatus.label}</p>
            {upload.processing_error && (
              <p className="text-sm text-red-600 mt-1">{upload.processing_error}</p>
            )}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="card">
        <h3 className="text-lg font-semibold text-navy-800 mb-4">Details</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">File Size</span>
            <p className="font-medium text-navy-700">
              {upload.file_size_bytes ? (upload.file_size_bytes / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-gray-500">Created</span>
            <p className="font-medium text-navy-700">{new Date(upload.created_at).toLocaleString()}</p>
          </div>
          <div>
            <span className="text-gray-500">Selected Indices</span>
            <p className="font-medium text-navy-700">
              {upload.selected_indices?.join(', ').toUpperCase() || 'None'}
            </p>
          </div>
          <div>
            <span className="text-gray-500">Retain Raw Data</span>
            <p className="font-medium text-navy-700">{upload.retain_raw_indices ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </div>

      {/* Layers */}
      {layers.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-navy-800 mb-4">Generated Layers</h3>
          <div className="space-y-2">
            {layers.map(layer => (
              <div key={layer.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${layer.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span className="font-medium text-navy-700 uppercase">{layer.layer_name}</span>
                  <span className="text-xs text-gray-400">{layer.dtype}</span>
                  {layer.band_count && <span className="text-xs text-gray-400">{layer.band_count} bands</span>}
                </div>
                <span className="text-xs text-gray-400">
                  {layer.file_size_bytes ? (layer.file_size_bytes / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Map Button */}
      {upload.status === 'completed' && (
        <div className="flex justify-center">
          <button
            onClick={() => navigate(`/map/${upload.id}`)}
            className="btn-primary flex items-center gap-2 px-8 py-3"
          >
            <Map size={20} />
            View on Map
          </button>
        </div>
      )}
    </div>
  )
}
